import React, { useState } from 'react';
import { ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import { Text } from 'react-native-paper';
import QRCode from 'react-native-qrcode-svg';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useAppColors } from '../../hooks/useAppColors';
import { responsiveContentStyle } from '../../hooks/useResponsiveLayout';
import { useAuthStore } from '../../stores/authStore';
import { useRoleStore } from '../../stores/roleStore';
import { useTimetable } from '../../hooks/useTimetable';
import { useActiveMembership } from '../../hooks/usePayments';
import AppHeader from '../../components/common/AppHeader';
import { ClosedBanner, ClassCard } from '../../components/classes';
import { useTranslation } from 'react-i18next';

interface QRMember {
  id: string;
  name: string;
}

export default function CheckInScreen() {
  const { t } = useTranslation();
  const colors = useAppColors();
  const user = useAuthStore((s) => s.user);
  const activeProfile = useRoleStore((s) => s.activeProfile);
  const userId = user?.id || '';
  const [qrIndex, setQrIndex] = useState(0);

  const todayDateStr = new Date().toISOString().split('T')[0];
  const { data: timetable } = useTimetable({ target_date: todayDateStr });
  const { data: activeMembership } = useActiveMembership();
  const pendingVerificationPaymentId = useAuthStore((s) => s.pendingVerificationPaymentId);
  const hasPausedMembership = useAuthStore((s) => s.hasPausedMembership);
  const pausedMembership = useAuthStore((s) => s.pausedMembership);

  // Build list of members with QR codes from active membership data
  // Filter out paused memberships - they should not show QR codes
  const qrMembers: QRMember[] = (activeMembership?.members || [])
    .filter((member: any) => member.membership?.status !== 'paused')
    .map((member: any) => ({
      id: member.user_id,
      name: member.user_name,
    }));

  const hasActiveMembership = (activeMembership?.members?.length || 0) > 0;
  const isPendingActivation = !!pendingVerificationPaymentId && !hasActiveMembership;

  // Check if current member (or main user) has paused membership
  const currentMemberData = (activeMembership?.members || [])[qrIndex];
  const isCurrentMemberPaused = currentMemberData?.membership?.status === 'paused';
  const isMainUserPaused = hasPausedMembership && !activeProfile;

  const currentMember = qrMembers[qrIndex] || qrMembers[0];
  const athleteId = currentMember?.id || userId;
  // QR encodes the full user id (UUID) so the desk reader can resolve the
  // member unambiguously. The short label below the QR is purely cosmetic.
  const qrValue = athleteId;
  const shortLabel = athleteId
    ? `${athleteId.slice(0, 8).toUpperCase()}…${athleteId.slice(-4).toUpperCase()}`
    : '—';
  const canSwipeLeft = qrIndex > 0;
  const canSwipeRight = qrIndex < qrMembers.length - 1;

  // Determine if current member is on a kids plan (age < 15)
  // currentMemberData is already declared above for paused check
  const isKidsPlan = currentMemberData?.membership?.plan?.is_kids || false;

  // Get plan time and day restrictions for current member
  const planStartTime = currentMemberData?.membership?.plan?.allowed_start_time;
  const planEndTime = currentMemberData?.membership?.plan?.allowed_end_time;
  const hasTimeRestrictions = !!(planStartTime || planEndTime);

  // Get day of week restrictions
  const planAllowedDays = currentMemberData?.membership?.plan?.allowed_days_of_week;
  const hasDayRestrictions = !!(planAllowedDays && planAllowedDays.length > 0);

  // Helper to check if class time is within plan restrictions
  const isClassWithinAllowedHours = (classStartTime: string): boolean => {
    if (!hasTimeRestrictions) return true;
    if (!classStartTime) return true;

    const classTime = classStartTime.slice(0, 5); // Get HH:MM format

    // Handle only start time restriction
    if (planStartTime && !planEndTime) {
      return classTime >= planStartTime.slice(0, 5);
    }
    // Handle only end time restriction
    if (!planStartTime && planEndTime) {
      return classTime <= planEndTime.slice(0, 5);
    }
    // Handle both start and end time
    if (planStartTime && planEndTime) {
      const start = planStartTime.slice(0, 5);
      const end = planEndTime.slice(0, 5);
      // Normal range (e.g., 06:00-18:00)
      if (start <= end) {
        return classTime >= start && classTime <= end;
      }
      // Overnight range (e.g., 22:00-06:00)
      return classTime >= start || classTime <= end;
    }
    return true;
  };

  // Find next class from timetable
  const now = new Date();
  const dayNames = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const today = dayNames[now.getDay()] as import('../../api/payments').DayOfWeek;
  const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

  // Check if today is in allowed days
  const isTodayAllowed = hasDayRestrictions ? planAllowedDays?.includes(today) : true;

  const todayClasses = (timetable || [])
    .filter((e: any) => e.day_of_week === today)
    .filter((e: any) => {
      // Filter by class type based on age: kids plan -> kids classes only, adult plan -> non-kids classes only
      if (isKidsPlan) {
        return e.class_type === 'kids';
      } else {
        return e.class_type !== 'kids';
      }
    })
    .sort((a: any, b: any) => (a.start_time || '').localeCompare(b.start_time || ''));

  // Show upcoming classes AND classes currently in progress
  const upcomingClasses = todayClasses.filter((c: any) => {
    const start = c.start_time || '';
    const end = c.end_time || '';
    // Include if: class hasn't started yet, OR class is currently in progress
    return start >= currentTime || (start <= currentTime && end > currentTime);
  });

  // Filter to only show classes within allowed hours
  let allowedClasses = hasTimeRestrictions
    ? upcomingClasses.filter((c: any) => isClassWithinAllowedHours(c.start_time))
    : upcomingClasses;

  // If today is not in allowed days, show no classes
  if (hasDayRestrictions && !isTodayAllowed) {
    allowedClasses = [];
  }

  // Check if member has restricted access (some classes are outside their hours or today is restricted)
  const hasRestrictedAccess = (hasTimeRestrictions && upcomingClasses.length > 0 && allowedClasses.length < upcomingClasses.length) ||
                               (hasDayRestrictions && !isTodayAllowed);

  // Check if all classes are closed for the day
  const allClosed = todayClasses.length > 0 && todayClasses.every((c: any) => c.is_closed);
  const closedReason = allClosed && todayClasses[0]?.closed_reason ? todayClasses[0].closed_reason : null;
  const isPublicHoliday = allClosed && todayClasses[0]?.is_public_holiday;

  return (
    <View style={[styles.screen, { backgroundColor: colors.bg }]}>
      <AppHeader />
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[styles.content, responsiveContentStyle]}
        showsVerticalScrollIndicator={false}
      >
        {/* Member paging controls - at screen level */}
        {qrMembers.length > 1 && (
          <View style={styles.pagingRow}>
            <TouchableOpacity
              onPress={() => setQrIndex((i) => Math.max(0, i - 1))}
              disabled={!canSwipeLeft}
              style={[styles.pagingBtn, { opacity: canSwipeLeft ? 1 : 0.3 }]}
            >
              <Icon name="chevron-left" size={24} color={colors.text} />
            </TouchableOpacity>
            <Text style={[styles.pagingLabel, { color: colors.textSecondary }]}>
              {currentMember?.name || t('home.scanAtDesk')}
            </Text>
            <TouchableOpacity
              onPress={() => setQrIndex((i) => Math.min(qrMembers.length - 1, i + 1))}
              disabled={!canSwipeRight}
              style={[styles.pagingBtn, { opacity: canSwipeRight ? 1 : 0.3 }]}
            >
              <Icon name="chevron-right" size={24} color={colors.text} />
            </TouchableOpacity>
          </View>
        )}

        {/* QR Check-in Card - hidden when pending activation or paused */}
        {isPendingActivation ? (
          <View
            style={[
              styles.qrCard,
              { backgroundColor: colors.card, borderColor: colors.border },
            ]}
          >
            <Icon name="clock-check-outline" size={48} color={colors.warning} />
            <Text style={[styles.qrTitle, { color: colors.text, marginTop: 12 }]}>
              {t('home.pendingActivation.title')}
            </Text>
            <Text style={[styles.emptyText, { color: colors.textSecondary, marginTop: 4 }]}>
              {t('home.pendingActivation.body')}
            </Text>
          </View>
        ) : isMainUserPaused || isCurrentMemberPaused ? (
          <View
            style={[
              styles.qrCard,
              { backgroundColor: colors.card, borderColor: colors.border },
            ]}
          >
            <Icon name="pause-circle-outline" size={48} color={colors.warning} />
            <Text style={[styles.qrTitle, { color: colors.text, marginTop: 12 }]}>
              {t('home.membershipPaused.title', 'Membership Paused')}
            </Text>
            <Text style={[styles.emptyText, { color: colors.textSecondary, marginTop: 4 }]}>
              {t('home.membershipPaused.body', 'Your membership is currently paused.\n\nCheck-in is not available during the pause period.')}
            </Text>
          </View>
        ) : qrMembers.length > 0 && (
          <View
            style={[
              styles.qrCard,
              { backgroundColor: colors.card, borderColor: colors.border },
            ]}
          >
            <Text style={[styles.qrTitle, { color: colors.text }]}>{t('home.checkIn')}</Text>
            <View style={[styles.qrBox, { backgroundColor: colors.qrBg }]}>
              {qrValue ? (
                <QRCode value={qrValue} size={240} backgroundColor="white" color="#000000" />
              ) : (
                <Icon name="qrcode" size={96} color="#000000" />
              )}
            </View>
            <Text style={[styles.qrId, { color: colors.textTertiary }]}>
              {t('home.idPrefix')}: {shortLabel}
            </Text>
          </View>
        )}

        {/* Next Class - specific to selected member */}
        <View style={styles.section}>
          <Text
            style={[styles.sectionLabel, { color: colors.textSecondary }]}
          >
            {t('home.nextClass')}
          </Text>
          {(hasTimeRestrictions || hasDayRestrictions) && (
            <View style={[styles.timeRestrictionBanner, { backgroundColor: colors.warning + '20', borderColor: colors.warning }]}>
              <Icon name="clock-outline" size={16} color={colors.warning} />
              <Text style={[styles.timeRestrictionText, { color: colors.textSecondary }]}>
                {hasDayRestrictions && !isTodayAllowed
                  ? t('home.dayRestrictionToday', { defaultValue: 'Your plan does not allow classes today' })
                  : t('home.timeRestriction', { start: planStartTime?.slice(0, 5) || '--:--', end: planEndTime?.slice(0, 5) || '--:--', defaultValue: `Plan limited to ${planStartTime?.slice(0, 5) || '--:--'} - ${planEndTime?.slice(0, 5) || '--:--'}` })}
              </Text>
            </View>
          )}
          {allowedClasses.length > 0 ? (
            <View style={{ gap: 12 }}>
              {/* Full-day closed banner */}
              {allClosed && (
                <ClosedBanner isPublicHoliday={isPublicHoliday} reason={closedReason} />
              )}
              {allowedClasses.map((cls: any, idx: number) => (
                <ClassCard
                  key={`${cls.schedule_id || cls.id || idx}-${cls.start_time}`}
                  entry={cls}
                  variant="compact"
                  showBadges={!allClosed}
                  targetDate={new Date()}
                />
              ))}
            </View>
          ) : (
            <View
              style={[
                styles.nextClassCard,
                { backgroundColor: colors.card, borderColor: colors.border },
              ]}
            >
              <Text
                style={[styles.emptyText, { color: colors.textTertiary }]}
              >
                {hasDayRestrictions && !isTodayAllowed
                  ? t('home.planNotValidToday', { defaultValue: 'Your membership plan is not valid for classes today' })
                  : hasTimeRestrictions && upcomingClasses.length > 0
                    ? t('home.noClassesInAllowedHours', { defaultValue: 'No classes available in your plan\'s allowed hours' })
                    : t('home.noClassesToday')}
              </Text>
            </View>
          )}
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1 },
  scroll: { flex: 1 },
  content: { padding: 24, paddingBottom: 120, gap: 24, alignItems: 'stretch' },

  // QR Card
  qrCard: {
    borderRadius: 24,
    padding: 24,
    borderWidth: 1,
    alignItems: 'center',
  },
  qrTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 4,
  },
  qrSubtitle: {
    fontSize: 14,
    marginBottom: 24,
  },
  qrBox: {
    padding: 16,
    borderRadius: 12,
    marginBottom: 16,
  },
  qrId: {
    fontSize: 12,
  },

  // Screen-level paging controls
  pagingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 12,
    backgroundColor: 'transparent',
  },
  pagingBtn: {
    padding: 8,
  },
  pagingLabel: {
    fontSize: 15,
    fontWeight: '600',
    flex: 1,
    textAlign: 'center',
  },

  // Sections
  section: {
    gap: 12,
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: '600',
    letterSpacing: 1,
  },

  // Next Class Card
  nextClassCard: {
    borderRadius: 16,
    padding: 20,
    borderWidth: 1,
  },
  nextClassRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  timeBox: {
    borderRadius: 12,
    padding: 12,
    alignItems: 'center',
    minWidth: 60,
  },
  timeLabel: {
    fontSize: 10,
    fontWeight: '600',
  },
  timeValue: {
    fontSize: 18,
    fontWeight: '700',
  },
  nextClassInfo: {
    flex: 1,
    gap: 4,
  },
  className: {
    fontSize: 16,
    fontWeight: '600',
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  metaText: {
    fontSize: 12,
  },
  emptyText: {
    textAlign: 'center',
    fontSize: 14,
    paddingVertical: 12,
  },
  // Time restriction banner
  timeRestrictionBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
  },
  timeRestrictionText: {
    fontSize: 12,
    flex: 1,
  },
});
